<template>
    <div>
        <loader v-if="isLoading" />
        <b-table-simple v-else borderless hover class="list">
            <b-thead>
                <b-tr>
                    <b-th>Title:</b-th>
                    <b-th>Patient:</b-th>
                    <b-th>Reference:</b-th>
                    <b-th>Author:</b-th>
                    <b-th>Added:</b-th>
                    <b-th></b-th>
                </b-tr>
            </b-thead>
            <b-tbody>
                <b-tr v-for="(doc, i) in documents" :key="i">
                    <b-td>
                        <input type="hidden" name="id" :value="doc.id" />
                        <router-link :to="`/client/letter/${doc.encoded_id}`">
                            {{ doc.title || "Letter" }}
                        </router-link>
                    </b-td>

                    <b-td>
                        <patient-name
                            v-if="!isPatientEmpty(doc.patient)"
                            :use-router="USE_PATIENT_MODULE_VUE"
                            :patient="doc.patient"
                        />
                    </b-td>

                    <b-td>
                        <span v-if="doc.soap && doc.soap.id">
                            {{ doc.soap.label }}
                        </span>
                        <span v-else>-</span>
                    </b-td>

                    <b-td>
                        {{ doc.veterinarian.full_name }}
                    </b-td>

                    <b-td>
                        {{ _setDateDisplay(doc.date) }}
                    </b-td>

                    <b-td
                        class="options"
                        v-if="isLoadingPrintRecordId !== doc.id"
                    >
                        <b-dropdown
                            v-if="
                                doc.isEditable &&
                                doc.isEditable.status &&
                                (_hasAccess('client.letter.update') ||
                                    _hasAccess('client.letter.email') ||
                                    _hasAccess('client.letter.print') ||
                                    _hasAccess('client.letter.delete'))
                            "
                            size="sm"
                            variant="link"
                            right
                            no-caret
                        >
                            <template v-slot:button-content>
                                <i class="cus-cog-down"></i>
                            </template>
                            <b-dropdown-item
                                v-if="_hasAccess('client.letter.edit')"
                                :to="`/client/letter/${doc.encoded_id}`"
                            >
                                <i class="cus-page-white-edit"></i>
                                Edit Letter
                            </b-dropdown-item>

                            <b-dropdown-item
                                v-if="_hasAccess('client.letter.email')"
                                @click="emailLetter(doc)"
                            >
                                <i class="cus-email"></i> Email Letter
                            </b-dropdown-item>

                            <b-dropdown-item
                                v-if="_hasAccess('client.letter.print')"
                                @click="printLetter(doc.id)"
                            >
                                <i class="cus-printer"></i> Print Letter
                            </b-dropdown-item>

                            <b-dropdown-item
                                v-if="_hasAccess('client.letter.delete')"
                                @click="promptDelete('letter', doc.id)"
                            >
                                <i class="cus-delete"></i> Delete Letter
                            </b-dropdown-item>
                        </b-dropdown>
                    </b-td>
                    <b-td class="options" v-else>
                        <moon-loader color="black" size="16px" />
                    </b-td>
                </b-tr>
                <b-tr v-if="documents.length === 0">
                    <b-td colspan="6">
                        <i>No records available</i>
                    </b-td>
                </b-tr>
            </b-tbody>
        </b-table-simple>

        <b-pagination
            v-if="showPagination"
            class="vet-pagination"
            align="right"
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            prev-text="Prev"
            next-text="Next"
            :hide-goto-end-buttons="true"
            @change="changePage"
        />

        <modal-delete-document
            ref="modalDeleteDocument"
            @onSuccess="afterDeleted"
        />

        <modal-email-letter ref="modalEmailLetter" />
    </div>
</template>

<script>
import ClientDocument from "@/services/client-documents";
import Loader from "@/components/elements/loader-list";
import ModalDeleteDocument from "@/components/client-documents/document-delete-attachment-modal";
import ModalEmailLetter from "@/components/client-documents/document-email-letter-modal";
import MoonLoader from "vue-spinner/src/MoonLoader.vue";
import PatientName from "@/components/commons/patient-name";
import { isEmpty } from "lodash";

export default {
    components: {
        Loader,
        ModalDeleteDocument,
        ModalEmailLetter,
        MoonLoader,
        PatientName
    },
    data() {
        return {
            documents: [],
            isLoading: false,
            clientId: this.$store.state.client.client_id,
            isLoadingPrintRecordId: false,
            currentPage: 1,
            totalRows: 0,
            perPage: 0,
            showPagination: false,
            CI_APPS: process.env.CI_APPS
        };
    },
    computed: {
        userId() {
            return this.$route.params.id;
        }
    },
    mounted() {
        this.getDocuments(1);
    },
    methods: {
        async getDocuments(page) {
            try {
                this.isLoading = true;

                const { resources, meta } = await ClientDocument.getDocuments({
                    type: "letter",
                    page: page,
                    clientId: this.$store.state.client.client_id
                });

                this.documents = resources;
                this.isLoading = false;

                // TODO: enhancement : Below meta can be deleted as already used the endpoint of webform count but need also remove from backend and handle the endpoint each time add/delete letter                // update tab menu items counts
                if (meta) {
                    // pagination
                    this.currentPage = meta.currentPage;
                    this.totalRows = meta.total;
                    this.perPage = meta.perPage;
                    this.showPagination = meta.lastPage > 1; // show pagination if there's more than 1 page

                    // counts
                    if (meta.count) {
                        this.$emit("updateCounts", {
                            all: meta.count.all_count.toString(),
                            attachment: meta.count.attachment_count.toString(),
                            letter: meta.count.letter_count.toString(),
                            webform: meta.count.webform_count.toString()
                        });
                    }
                }
            } catch (e) {
                console.log(e);
            }
        },
        properDate(rawDate) {
            return this._toDateTime(rawDate).toFormat(this._getLuxonFormat());
        },
        promptDelete(type, documentId) {
            this.$refs.modalDeleteDocument.open({
                type: type,
                id: documentId
            });
        },
        afterDeleted() {
            this.getDocuments();
        },
        emailLetter(document) {
            this.$refs.modalEmailLetter.open(document);
        },
        async printLetter(documentId) {
            this.isLoadingPrintRecordId = documentId;
            const pdfPath = await ClientDocument.getPDFLetterURI(documentId);
            this.isLoadingPrintRecordId = null;

            if (pdfPath) {
                window.open(pdfPath, "_blank");
            } else {
                this._message("Error: Unable to print", "danger");
            }
        },
        changePage(nextPage) {
            this.getDocuments(nextPage);
        },
        isPatientEmpty(patient) {
            return isEmpty(patient);
        }
    }
};
</script>

<style scoped>
td.options {
    width: 50px;
    text-align: right;
}
.vet-pagination {
    margin-top: 50px;
}
</style>
